import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import { servicePages, SITE_URL } from "@/data/servicePages";

export default function ServicePage({ slug }) {
  const page = servicePages[slug];
  const url = `${SITE_URL}/${slug}`;

  // Structured data for SEO / AEO / GEO: breadcrumb, service and FAQ schema.
  const jsonLd = useMemo(() => {
    if (!page) return [];
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/#services` },
          { "@type": "ListItem", position: 3, name: page.name, item: url },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: page.name,
        serviceType: page.category,
        description: page.metaDescription,
        url,
        provider: {
          "@type": "FinancialService",
          name: "Aurelius Family Office",
          url: `${SITE_URL}/`,
        },
        areaServed: ["Dubai", "Switzerland", "Singapore", "London", "United States"],
        audience: { "@type": "Audience", audienceType: "High-net-worth and ultra-high-net-worth families" },
        offers: page.includes.map((i) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: i.title } })),
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: page.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
  }, [page, slug, url]);

  useSEO({
    title: page?.metaTitle,
    description: page?.metaDescription,
    keywords: page?.keywords,
    canonical: url,
    ogTitle: `${page?.name} | Aurelius Family Office`,
    jsonLd,
  });

  useEffect(() => window.scrollTo(0, 0), [slug]);

  if (!page) return null;

  return (
    <div className="bg-paper">
      <Navbar />

      {/* Hero */}
      <section data-testid="service-hero" className="relative bg-brand">
        {page.img && (
          <img src={page.img} alt={page.h1} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-brand/85" />
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "radial-gradient(900px 500px at 85% -10%, #ffffff, transparent 60%), radial-gradient(700px 500px at 0% 110%, #ffffff, transparent 55%)",
          }}
        />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 pt-36 pb-20">
          <Link
            to="/#services"
            data-testid="service-breadcrumb"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-brand-500 transition-colors mb-8"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Services · Aurelius Family Office
          </Link>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-500 mb-4">{page.category}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight max-w-3xl">
            {page.h1}
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">{page.intro}</p>
          <div className="mt-10 flex flex-wrap gap-10">
            {page.stats.map(([v, l]) => (
              <div key={l}>
                <p className="font-serif-display text-3xl text-white">{v}</p>
                <p className="text-xs uppercase tracking-widest text-slate-400 mt-1">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AEO quick-answer box */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <div
              data-testid="service-answer"
              className="border-s-2 border-brand-600 bg-slate-50 p-8 md:p-10 max-w-4xl"
            >
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-3">In short</p>
              <p className="font-outfit text-lg md:text-2xl text-slate-900 leading-relaxed">{page.answer}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What's included */}
      <section className="bg-white pb-20 md:pb-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">Scope</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-medium tracking-tight text-slate-950 mb-14">
              {page.includesLabel}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-s border-slate-200">
            {page.includes.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <div
                  data-testid={`service-include-${i}`}
                  className="border-b border-e border-slate-200 p-8 lg:p-10 h-full hover:bg-slate-50 transition-colors"
                >
                  <Check className="w-6 h-6 text-brand-700 mb-5" strokeWidth={1.75} />
                  <h3 className="font-outfit text-xl font-medium text-slate-950 mb-3">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Approach / process */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">Approach</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-medium tracking-tight text-slate-950 mb-14">
              {page.processLabel}
            </h2>
          </Reveal>
          <div className="border-s border-slate-200">
            {page.process.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div data-testid={`service-process-${i}`} className="relative ps-10 pb-12 last:pb-0">
                  <span className="absolute -start-[5px] top-1.5 w-[9px] h-[9px] bg-brand-600" />
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <h3 className="font-outfit text-xl font-medium text-slate-950">{s.title}</h3>
                    <span className="text-xs uppercase tracking-widest text-brand-700">{s.time}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-xl">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">FAQ</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-medium tracking-tight text-slate-950">
              {page.name} questions
            </h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              Clear answers to the questions families and advisors ask most.
            </p>
          </Reveal>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {page.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-slate-200">
                  <AccordionTrigger
                    data-testid={`service-faq-trigger-${i}`}
                    className="text-start font-outfit text-base md:text-lg font-medium text-slate-950 hover:text-brand-700 hover:no-underline py-6"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-slate-600 leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Related services (internal linking for SEO/GEO) */}
      <section className="bg-slate-50 py-20 md:py-24 border-t border-slate-200">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-10">Related services</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-s border-slate-200">
            {page.related.map((rslug, i) => {
              const r = servicePages[rslug];
              if (!r) return null;
              return (
                <Reveal key={rslug} delay={i * 0.08}>
                  <Link
                    to={`/${rslug}`}
                    data-testid={`service-related-${i}`}
                    className="group flex flex-col justify-between gap-8 border-b border-e border-slate-200 p-8 lg:p-10 h-full bg-white hover:bg-slate-50 transition-colors"
                  >
                    <div>
                      <p className="text-[11px] uppercase tracking-widest text-brand-700 mb-3">{r.category}</p>
                      <h3 className="font-outfit text-xl font-medium text-slate-950 mb-2">{r.name}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{r.metaDescription}</p>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-slate-400 group-hover:text-brand-700 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand py-20 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <Reveal>
            <h2 className="font-serif-display text-3xl md:text-4xl font-light text-white">
              Discuss {page.name.toLowerCase()} for your family.
            </h2>
            <p className="mt-3 text-slate-400">Confidential, no-obligation conversation with a senior partner.</p>
          </Reveal>
          <Link
            to="/#consultation"
            data-testid="service-cta-button"
            className="bg-white hover:bg-brand-50 transition-colors text-brand px-8 py-4 text-sm tracking-widest uppercase rounded-none shrink-0"
          >
            Request Consultation
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
